import { castArray, compact, isEmpty, uniq } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  DEFAULT_SORT_ORDER,
  isNdjsonContentType,
  parseNdjson,
  streamNdjsonRequest,
  useStableTimeWindow,
  useStreamQuery,
} from './streaming';

// v2 User Events endpoints. Not available in the generated humarestclient SDK yet, so they are
// invoked through the existing v1 `userEventsApi.apiClient` (same base path and auth headers).
export const USER_EVENTS_V2_ENDPOINTS = {
  byTypesAndTimeRange:
    '/hacloud/api/v2/user_events/{useruuid}/eventtypes/startdatetime/{startdatetime}/enddatetime/{enddatetime}',
  deviceEventsByTypesAndTimeRange:
    '/hacloud/api/v2/user_events/{useruuid}/device/startdatetime/{startdatetime}/enddatetime/{enddatetime}',
};

const requireParams = (params, operation) => {
  Object.entries(params).forEach(([name, value]) => {
    if (value === undefined || value === null) {
      throw new Error(`Missing the required parameter '${name}' when calling ${operation}`);
    }
  });
};

// Stable identity for a user event: v2 events carry a unique `id`; v1 events only `message_id`.
export const getUserEventKey = (event) => event?.id ?? event?.message_id ?? null;

export const USER_EVENTS_DEFAULT_SORT = {
  sortBy: 'utc_timestamp_inms',
  sortOrder: DEFAULT_SORT_ORDER,
};

const toUserEventsArray = (data) => (Array.isArray(data) ? data : data?.user_events || []);

// v2 streams one event per line as `application/x-ndjson` (no `user_events` wrapper), which the
// v1 ApiClient hands back as a raw string. Normalise it to the v1 `{ user_events: [...] }` shape.
export const toUserEventsResponse = (data, response) => {
  const contentType = response?.headers?.['content-type'] || '';
  const raw = typeof data === 'string' ? data : response?.text;

  if (isNdjsonContentType(contentType) || typeof data === 'string') {
    return { user_events: raw ? parseNdjson(raw) : [] };
  }
  if (Array.isArray(data)) {
    return { user_events: data };
  }
  return { ...data, user_events: data?.user_events || [] };
};

// PUTs to a v2 user_events time-range endpoint and hands the callback
// `(error, { user_events: [...] }, response)`, the same contract as the v1 SDK methods.
const putUserEventsV2 = (userEventsApi, path, pathParams, body, callback) =>
  userEventsApi.apiClient.callApi(
    path,
    'PUT',
    pathParams,
    {},
    {},
    {},
    body,
    [],
    ['application/json'],
    ['application/json'],
    Object,
    null,
    (error, data, response) => {
      if (error) {
        callback(error, data, response);
        return;
      }

      let normalized;
      try {
        normalized = toUserEventsResponse(data, response);
      } catch (parseError) {
        callback(parseError, null, response);
        return;
      }
      callback(null, normalized, response);
    }
  );

/**
 * PUT /hacloud/api/v2/user_events/{useruuid}/eventtypes/startdatetime/{startdatetime}/enddatetime/{enddatetime}
 * Drop-in replacement for the v1 `userEventsApi.userEventsByTypesAndTimeRange`.
 */
export const userEventsByTypesAndTimeRange = (
  userEventsApi,
  { useruuid, startdatetime, enddatetime, eventtypes = [], details = true },
  callback
) => {
  requireParams({ useruuid, startdatetime, enddatetime }, 'userEventsByTypesAndTimeRange');

  return putUserEventsV2(
    userEventsApi,
    USER_EVENTS_V2_ENDPOINTS.byTypesAndTimeRange,
    { useruuid, startdatetime, enddatetime },
    { eventtypes, details },
    callback
  );
};

/**
 * PUT /hacloud/api/v2/user_events/{useruuid}/device/startdatetime/{startdatetime}/enddatetime/{enddatetime}
 * Drop-in replacement for the v1 `userEventsApi.userDeviceEventsByTypesAndTimeRange`.
 */
export const userDeviceEventsByTypesAndTimeRange = (
  userEventsApi,
  { useruuid, startdatetime, enddatetime, deviceuuid = [], eventtypes = [] },
  callback
) => {
  requireParams({ useruuid, startdatetime, enddatetime }, 'userDeviceEventsByTypesAndTimeRange');

  return putUserEventsV2(
    userEventsApi,
    USER_EVENTS_V2_ENDPOINTS.deviceEventsByTypesAndTimeRange,
    { useruuid, startdatetime, enddatetime },
    { deviceuuid, eventtypes },
    callback
  );
};

/**
 * Streaming variant of `userEventsByTypesAndTimeRange`: yields batches of events as they arrive.
 * @param {Object} userEventsApi `state.apis.userEventsApi`
 * @param {Object} params Same params as `userEventsByTypesAndTimeRange`.
 * @param {{ signal?: AbortSignal, batchMs?: number, maxBatchSize?: number }} [streamOptions]
 * @returns {AsyncGenerator<Array<Object>>}
 */
export const streamUserEventsByTypesAndTimeRange = (
  userEventsApi,
  { useruuid, startdatetime, enddatetime, eventtypes = [], details = true },
  streamOptions = {}
) => {
  requireParams({ useruuid, startdatetime, enddatetime }, 'streamUserEventsByTypesAndTimeRange');

  return streamNdjsonRequest({
    ...streamOptions,
    apiClient: userEventsApi?.apiClient,
    path: USER_EVENTS_V2_ENDPOINTS.byTypesAndTimeRange,
    method: 'PUT',
    pathParams: { useruuid, startdatetime, enddatetime },
    body: { eventtypes, details },
    jsonFallback: toUserEventsArray,
  });
};

/**
 * Streaming variant of `userDeviceEventsByTypesAndTimeRange`: yields batches of events as they
 * arrive. `deviceuuid` accepts several devices, so one request can replace a request per device.
 * @param {Object} userEventsApi `state.apis.userEventsApi`
 * @param {Object} params Same params as `userDeviceEventsByTypesAndTimeRange`.
 * @param {{ signal?: AbortSignal, batchMs?: number, maxBatchSize?: number }} [streamOptions]
 * @returns {AsyncGenerator<Array<Object>>}
 */
export const streamUserDeviceEventsByTypesAndTimeRange = (
  userEventsApi,
  { useruuid, startdatetime, enddatetime, deviceuuid = [], eventtypes = [] },
  streamOptions = {}
) => {
  requireParams(
    { useruuid, startdatetime, enddatetime },
    'streamUserDeviceEventsByTypesAndTimeRange'
  );

  return streamNdjsonRequest({
    ...streamOptions,
    apiClient: userEventsApi?.apiClient,
    path: USER_EVENTS_V2_ENDPOINTS.deviceEventsByTypesAndTimeRange,
    method: 'PUT',
    pathParams: { useruuid, startdatetime, enddatetime },
    body: { deviceuuid, eventtypes },
    jsonFallback: toUserEventsArray,
  });
};

// ---------------------------------------------------------------------------------------------
// React hooks (tanstack-query) for the v2 user-events streams.
//
// Plug-and-play: they return the same `{ data, isLoading, isError }` contract as a normal request
// (see `useStreamQuery`), resolve and stabilise the time window themselves (`days` or explicit
// dates), and by default keep only the requested event types. A component only renders `data`.
// ---------------------------------------------------------------------------------------------

const USER_EVENT_TYPE_FIELD = 'message_type';

const toSortedUniqueList = (value) => uniq(compact(castArray(value ?? []))).sort();

// Memoised by content, so callers can pass inline arrays without changing the query key or filter.
const useSortedUniqueList = (value) => {
  const list = toSortedUniqueList(value);
  const contentKey = list.join('\u0000');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => list, [contentKey]);
};

// Keeps only the requested event types unless the caller passes its own `filter` (or `null`).
const useEventTypeFilter = (types, filter) =>
  useMemo(() => {
    if (filter !== undefined) return filter || undefined;
    if (isEmpty(types)) return undefined;
    const allowed = new Set(types);
    return (event) => allowed.has(event?.[USER_EVENT_TYPE_FIELD]);
  }, [types, filter]);

/**
 * Streams v2 device events (application/x-ndjson) for one or more devices. Rows appear as they
 * arrive, de-duplicated by event key and kept sorted by `sortBy` (newest first by default).
 *
 * @example
 *   const { data, isLoading, isError } = useUserDeviceEventsStream({
 *     useruuid, deviceuuid: deviceUuids, eventTypes, days: 7,
 *   });
 *
 * @param {Object} params
 * @param {string} params.useruuid
 * @param {string|string[]} params.deviceuuid One device or several (a single request for all).
 * @param {string|string[]} params.eventTypes
 * @param {number} [params.days] Look-back window ending now (fixed when the component mounts).
 * @param {*} [params.startDate] Explicit start (Date, moment, ISO string or epoch ms); overrides `days`.
 * @param {*} [params.endDate] Explicit end; defaults to now. Both are rounded to the minute.
 * @param {(event: Object) => boolean|null} [params.filter] Custom row filter. Default: only the
 *   requested `eventTypes`; `null` disables filtering.
 * @param {string} [params.sortBy='utc_timestamp_inms'] Field the data is kept sorted by.
 * @param {'asc'|'desc'} [params.sortOrder='desc']
 * @param {boolean} [params.enabled=true]
 * @param {number} [params.batchMs] See `readNdjson`.
 * @param {Object} [params.options] Remaining keys are passed to `useStreamQuery` (e.g. `deferred`,
 *   `staleTime`, `refetchMode`).
 * @returns See `useStreamQuery`, plus `getKey` (stable React key for a row) and the resolved
 *   `startDate` / `endDate`.
 */
export const useUserDeviceEventsStream = ({
  useruuid,
  deviceuuid,
  eventTypes,
  days,
  startDate,
  endDate,
  filter,
  sortBy = USER_EVENTS_DEFAULT_SORT.sortBy,
  sortOrder = USER_EVENTS_DEFAULT_SORT.sortOrder,
  enabled = true,
  batchMs,
  ...options
} = {}) => {
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const deviceUuids = useSortedUniqueList(deviceuuid);
  const types = useSortedUniqueList(eventTypes);
  const timeWindow = useStableTimeWindow({ startDate, endDate, days });
  const rowFilter = useEventTypeFilter(types, filter);

  const result = useStreamQuery(
    [
      'userDeviceEventsStream',
      useruuid,
      deviceUuids,
      types,
      timeWindow.startDate,
      timeWindow.endDate,
    ],
    ({ signal }) =>
      streamUserDeviceEventsByTypesAndTimeRange(
        userEventsApi,
        {
          useruuid,
          startdatetime: timeWindow.startDate,
          enddatetime: timeWindow.endDate,
          deviceuuid: deviceUuids,
          eventtypes: types,
        },
        { signal, batchMs }
      ),
    {
      sortBy,
      sortOrder,
      getKey: getUserEventKey,
      filter: rowFilter,
      ...options,
      enabled:
        enabled &&
        !!useruuid &&
        !isEmpty(deviceUuids) &&
        !isEmpty(types) &&
        !!timeWindow.startDate &&
        !!timeWindow.endDate,
    }
  );

  return { ...result, getKey: getUserEventKey, ...timeWindow };
};

/**
 * Streams v2 user events of the given types (application/x-ndjson). Same plug-and-play contract as
 * `useUserDeviceEventsStream`, without the device filter.
 *
 * @example
 *   const { data, isLoading, isError } = useUserEventsByTypesStream({ useruuid, eventTypes, days: 30 });
 *
 * @param {Object} params
 * @param {string} params.useruuid
 * @param {string|string[]} params.eventTypes
 * @param {number} [params.days] Look-back window ending now (fixed when the component mounts).
 * @param {*} [params.startDate] Explicit start; overrides `days`.
 * @param {*} [params.endDate] Explicit end; defaults to now.
 * @param {boolean} [params.details=true]
 * @param {(event: Object) => boolean|null} [params.filter] See `useUserDeviceEventsStream`.
 * @param {string} [params.sortBy='utc_timestamp_inms'] Field the data is kept sorted by.
 * @param {'asc'|'desc'} [params.sortOrder='desc']
 * @param {boolean} [params.enabled=true]
 * @param {number} [params.batchMs] See `readNdjson`.
 * @param {Object} [params.options] Remaining keys are passed to `useStreamQuery`.
 * @returns See `useUserDeviceEventsStream`.
 */
export const useUserEventsByTypesStream = ({
  useruuid,
  eventTypes,
  days,
  startDate,
  endDate,
  details = true,
  filter,
  sortBy = USER_EVENTS_DEFAULT_SORT.sortBy,
  sortOrder = USER_EVENTS_DEFAULT_SORT.sortOrder,
  enabled = true,
  batchMs,
  ...options
} = {}) => {
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const types = useSortedUniqueList(eventTypes);
  const timeWindow = useStableTimeWindow({ startDate, endDate, days });
  const rowFilter = useEventTypeFilter(types, filter);

  const result = useStreamQuery(
    ['userEventsByTypesStream', useruuid, types, timeWindow.startDate, timeWindow.endDate, details],
    ({ signal }) =>
      streamUserEventsByTypesAndTimeRange(
        userEventsApi,
        {
          useruuid,
          startdatetime: timeWindow.startDate,
          enddatetime: timeWindow.endDate,
          eventtypes: types,
          details,
        },
        { signal, batchMs }
      ),
    {
      sortBy,
      sortOrder,
      getKey: getUserEventKey,
      filter: rowFilter,
      ...options,
      enabled:
        enabled && !!useruuid && !isEmpty(types) && !!timeWindow.startDate && !!timeWindow.endDate,
    }
  );

  return { ...result, getKey: getUserEventKey, ...timeWindow };
};
